<script setup lang="ts">
    import { computed, ref, type Ref, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import koObsToRef from '@shared/utils/koObsToRef';
    import { saveBlockTargetAudience, loadBlockTargetAudiences, deleteBlockTargetAudience } from './Issues.access';
    import { type IssueBlockTargetAudience } from './interfaces';
    import List from '@shared/components/List/List.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { AlertTypes, bsAlert } from '@shared/components/BsAlerts';
    import FormInput from '@shared/components/Form/FormInput.vue';

    const emit = defineEmits<{
        (ev: 'close'): void;
    }>();

    type Show = false | 'create' | 'manage';

    const props = defineProps<{
        show: KnockoutObservable<Show>;
        updateBlock: (block: HTMLElement) => void;
    }>();

    const show: Ref<Show> = koObsToRef(props.show);

    const name = ref('');
    const conditionsAttr = ref('');
    const relationAttr = ref('');
    const targetAudiences: Ref<IssueBlockTargetAudience[]> = ref([]);
    const loading: Ref<boolean> = ref(false);
    const page = ref(0);
    const allLoaded = ref(false);

    const pageSize = 50;
    let loaded = false;

    watch(show, (newVal) => {
        if (newVal === 'create') {
            getAttributesFromBlock();
        }

        if (newVal) {
            !loaded && handleLoad();
        }
    });

    const handleSave = async () => {
        if (loading.value) return;
        loading.value = true;

        saveBlockTargetAudience(name.value, conditionsAttr.value, relationAttr.value)
            .then((result) => {
                targetAudiences.value?.push(result);
                name.value = '';
                show.value = 'manage';
                bsAlert({
                    type: AlertTypes.Success,
                });
            })
            .finally(() => {
                loading.value = false;
                loaded = true;
            });
    };

    const handleLoad = async () => {
        if (loading.value) return;
        loading.value = true;

        loadBlockTargetAudiences(page.value, pageSize)
            .then((result) => {
                if (result.length < pageSize) {
                    allLoaded.value = true;
                }
                targetAudiences.value = [...targetAudiences.value, ...result];
            })
            .finally(() => {
                loading.value = false;
                loaded = true;
            });
    };

    const handleLoadMore = async () => {
        page.value++;
        handleLoad();
    };

    const handleDelete = async (item: IssueBlockTargetAudience) => {
        if (loading.value) return;
        loading.value = true;

        deleteBlockTargetAudience(item.Id)
            .then(() => {
                item.Deleted = true;
                bsAlert({
                    type: AlertTypes.Success,
                });
            })
            .finally(() => {
                loading.value = false;
            });
    };

    const filteredTargetAudiences = computed(() => {
        return targetAudiences.value.filter((item) => !item.Deleted).sort((a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime());
    });

    const handleSelection = (item: IssueBlockTargetAudience) => {
        const data = JSON.parse(item.Data);
        const conditions = data.Conditions;
        const relation = data.Relation;

        updateAttributesForBlock(conditions, relation);
    };

    const activeBlockSelector = '.canvas .block.active';
    const conditionsAttributeName = 'ug-targetaudience-conditions';
    const relationAttributeName = 'ug-targetaudience-relation';

    const getAttributesFromBlock = () => {
        const currentBlock = document.querySelector(activeBlockSelector);
        if (!currentBlock) return;

        const conditionsAttrValue = currentBlock.getAttribute(conditionsAttributeName);
        const relationAttrValue = currentBlock.getAttribute(relationAttributeName);

        conditionsAttr.value = conditionsAttrValue || '';
        relationAttr.value = relationAttrValue || '';
    };

    const updateAttributesForBlock = (conditions: string, relation: string) => {
        const currentBlock = document.querySelector(activeBlockSelector) as HTMLElement;
        if (!currentBlock) return;

        currentBlock.setAttribute(conditionsAttributeName, conditions);
        currentBlock.setAttribute(relationAttributeName, relation);

        props.updateBlock(currentBlock);

        show.value = false;
        bsAlert({
            type: AlertTypes.Success,
        });
    };
</script>

<template>
    <Modal :show="show === 'create'" :title="$Resources.BlockTargetAudienceSave" @close="show = false">
        <template #content>
            <FormInput class="form-group" v-model="name" :label="$Resources.NameLabel" />
        </template>
        <template #button>
            <button class="btn btn-primary" :disabled="loading || !name" @click="handleSave">{{ $Resources.SaveButton }}</button>
        </template>
    </Modal>

    <Modal :show="show === 'manage'" :title="$Resources.BlockTargetAudienceManage" @close="show = false" size="lg">
        <template #content>
            <p>{{ $Resources.BlockTargetAudienceManageDescription }}</p>

            <List
                itemId="Id"
                displayAs="table"
                :items="filteredTargetAudiences"
                :serverSideFilter="true"
                :hideToolbar="true"
                :allLoaded="allLoaded"
                :loading="loading"
                :filterKeys="['Name']"
                :tableHeaders="[$Resources.NameLabel, $Resources.CreatedByLabel, $Resources.CreatedLabel, $Resources.RemoveButton]"
                @loadMore="handleLoadMore">
                <template #item="{ item }">
                    <td style="max-width: 300px">
                        <a
                            href="javascript:void(0)"
                            role="button"
                            v-popconfirm="{
                                heading: $Resources.BlockTargetAudienceReplaceHeading,
                                body: $Resources.BlockTargetAudienceReplaceMessage,
                                callback: () => handleSelection(item),
                                placement: 'top',
                            }">
                            {{ item.Name || '[' + $Resources.ValueIsMissing + ']' }}
                        </a>
                    </td>
                    <td>{{ item.CreatedBy.FirstName }} {{ item.CreatedBy.LastName }}</td>
                    <td class="small">{{ formatDateTime(item.Created) }}</td>
                    <td class="text-right">
                        <button
                            v-popconfirm="{
                                heading: $Resources.RemoveButton,
                                body: $Resources.GeneralConfirmMessage,
                                callback: () => handleDelete(item),
                                placement: 'top',
                            }"
                            class="btn btn-default btn-xs"
                            type="button">
                            <i class="glyphicon glyphicon-trash"></i>
                            <span class="sr-only">{{ $Resources.RemoveButton }}</span>
                        </button>
                    </td>
                </template>
            </List>
        </template>
    </Modal>
</template>
